import React, { useState } from 'react';
import { caseStudies } from '../data/mock';
import { Badge } from './ui/badge';
import { CheckCircle2, ChevronDown, ChevronUp, Target, Lightbulb, TrendingUp } from 'lucide-react';

const CaseStudies = () => {
  const [expandedStudy, setExpandedStudy] = useState(null);

  const toggleStudy = (id) => {
    setExpandedStudy(expandedStudy === id ? null : id);
  };

  return (
    <section id="case-studies" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-20">
          <span className="text-sm font-semibold tracking-widest text-gray-500 uppercase mb-4 block">
            Portfolio
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Case Studies</h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Real-world success stories showcasing strategic thinking and measurable results
          </p>
        </div>

        {/* Case Studies Grid */}
        <div className="space-y-20">
          {caseStudies.map((study, index) => (
            <article 
              key={study.id} 
              data-testid={`case-study-${study.id}`}
              className="group"
            >
              {/* Hero Image - Always First */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl mb-8">
                <div className="aspect-[21/9] overflow-hidden">
                  <img
                    src={study.image}
                    alt={study.title}
                    className="w-full h-full object-cover object-center transform group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                {/* Overlay with Tags */}
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-gray-900/40 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
                  <div className="flex flex-wrap gap-2 mb-4">
                    {study.tags.map((tag, idx) => (
                      <Badge 
                        key={idx} 
                        className="bg-white/20 backdrop-blur-sm text-white border-white/30 hover:bg-white/30"
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>
                  <h3 className="text-2xl md:text-4xl font-bold text-white mb-2">
                    {study.title}
                  </h3>
                  <p className="text-white/80 font-medium">{study.client}</p>
                </div>
              </div>

              {/* Content Blocks - Below Image */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Challenge Block */}
                <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-red-50 rounded-lg">
                      <Target className="w-5 h-5 text-red-600" />
                    </div>
                    <h4 className="font-bold text-gray-900 text-lg">Challenge</h4>
                  </div>
                  <p className="text-gray-600 leading-relaxed text-sm">
                    {study.challenge}
                  </p>
                </div>

                {/* Solution Block */}
                <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-blue-50 rounded-lg">
                      <Lightbulb className="w-5 h-5 text-blue-600" />
                    </div>
                    <h4 className="font-bold text-gray-900 text-lg">Solution</h4>
                  </div>
                  <p className="text-gray-600 leading-relaxed text-sm">
                    {study.solution}
                  </p>
                </div>

                {/* Results Block */}
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 shadow-lg border border-green-100 hover:shadow-xl transition-shadow duration-300">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <TrendingUp className="w-5 h-5 text-green-600" />
                    </div>
                    <h4 className="font-bold text-gray-900 text-lg">Results</h4>
                  </div>
                  <ul className="space-y-2">
                    {study.results.slice(0, 4).map((result, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 font-medium">{result}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Description */}
              <div className="mt-6 bg-gray-50 rounded-xl p-6 border border-gray-100">
                <p className="text-gray-700 leading-relaxed">{study.description}</p>
              </div>

              {/* Analytics Images - Expandable Section */}
              {study.analyticsImages && study.analyticsImages.length > 0 && (
                <div className="mt-6">
                  <button
                    onClick={() => toggleStudy(study.id)}
                    data-testid={`expand-analytics-${study.id}`}
                    className="w-full flex items-center justify-between p-4 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-colors duration-200"
                  >
                    <span className="font-semibold flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5" />
                      View Analytics & Data Proof ({study.analyticsImages.length} screenshots)
                    </span>
                    {expandedStudy === study.id ? (
                      <ChevronUp className="w-5 h-5" />
                    ) : (
                      <ChevronDown className="w-5 h-5" />
                    )}
                  </button>
                  
                  {expandedStudy === study.id && (
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 animate-in slide-in-from-top-2 duration-300">
                      {study.analyticsImages.map((analytics, idx) => (
                        <div 
                          key={idx} 
                          className="bg-white rounded-xl overflow-hidden shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300"
                        >
                          <div className="aspect-video overflow-hidden">
                            <img
                              src={analytics.url}
                              alt={analytics.caption}
                              className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                            />
                          </div>
                          <div className="p-4 bg-gray-50">
                            <p className="text-sm text-gray-600 leading-relaxed">
                              {analytics.caption}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;